import type { ReactNode } from 'react'
import { Alert, Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import RedeemIcon from '@mui/icons-material/Redeem'
import { useAdminObterProgramaCashbackQuery, type ProgramaCashbackLoja } from '../../../api'
import { ModuloNaoContratado } from '../../../components/admin/modulo-nao-contratado'
import { formatarPercentual } from '../../../lib/formato'
import { Secao } from '.'

/** Role do módulo de Cashback do Sischef — a mesma que o painel do Cashback exige. */
const ROLE_CASHBACK = 'ROLE_CASHBACK_CONFIG'

const DESCRICAO_COM_MODULO =
  'Define se os pedidos feitos pelo cardápio dão cashback ao cliente e se ele pode usar o cashback como desconto.'
const DESCRICAO_SEM_MODULO =
  'Com o Sischef Cashback, o cliente ganha de volta uma parte do valor de cada compra e usa como desconto na próxima.'

interface SecaoCashbackProps {
  /** Loja em edição: com o uso ligado, a seção mostra o limite do programa dela. */
  idConfig: number
  /** A empresa tem o módulo de Cashback? `undefined` enquanto o `/admin/me` não respondeu. */
  temModulo: boolean | undefined
  /** "Dar cashback": valor salvo na loja. `undefined` (resposta sem o campo) vale como ligado. */
  ativo: boolean | undefined
  aoMudar: (ativo: boolean) => void
  /** "Aceitar cashback como desconto": valor salvo na loja. `undefined` (resposta sem o campo) vale como desligado. */
  usoAtivo: boolean | undefined
  aoMudarUso: (usoAtivo: boolean) => void
}

export function SecaoCashback({
  idConfig,
  temModulo,
  ativo,
  aoMudar,
  usoAtivo,
  aoMudarUso,
}: SecaoCashbackProps): JSX.Element | null {
  // Só com o módulo há programa a consultar. Ao voltar para a aba consulta de novo: é no outro painel que o lojista muda.
  const { data: programa } = useAdminObterProgramaCashbackQuery(idConfig, { skip: temModulo !== true, refetchOnFocus: true })

  // Sem saber se a empresa tem o módulo, não mostra nada: evita piscar o switch ou o "não contratado" errado.
  if (temModulo === undefined) return null

  if (!temModulo) {
    return (
      <QuadroCashback descricao={DESCRICAO_SEM_MODULO}>
        <ModuloNaoContratado nomeModo="Sischef Cashback" role={ROLE_CASHBACK} tipo="modulo" />
      </QuadroCashback>
    )
  }

  return (
    <QuadroCashback descricao={DESCRICAO_COM_MODULO}>
      <Stack spacing={3}>
        <ChaveComExplicacao rotulo="Dar cashback nos pedidos do cardápio" ligado={ativo ?? true} aoMudar={aoMudar}>
          <Explicacao>
            <strong>Ligado:</strong> o cliente ganha cashback quando o pedido é concluído no Sischef. O pedido precisa
            ter o telefone ou o CPF dele; por isso totem e tablet normalmente não dão cashback.
          </Explicacao>
          <Explicacao>
            <strong>Desligado:</strong> os pedidos feitos pelo cardápio desta loja param de dar cashback. As vendas no
            caixa (PDV) continuam como estão, e o saldo que o cliente já ganhou não muda.
          </Explicacao>
          <Explicacao>Vale para os pedidos feitos depois de você salvar. Os que já foram enviados não mudam.</Explicacao>
        </ChaveComExplicacao>

        <ChaveComExplicacao
          rotulo="Aceitar cashback como desconto nos pedidos do cardápio"
          ligado={usoAtivo ?? false}
          aoMudar={aoMudarUso}
        >
          <Explicacao>
            <strong>Ligado:</strong> no delivery, o cliente que fez login no cardápio pode usar o saldo de cashback
            como desconto no pedido. Mesa, comanda, totem e tablet não usam.
          </Explicacao>
          <Explicacao>
            <strong>Desligado:</strong> o cliente não consegue usar o saldo pelo cardápio. No caixa (PDV) nada muda.
          </Explicacao>
          <Explicacao>Se o pedido for cancelado, o valor usado volta para o saldo do cliente.</Explicacao>
          {(usoAtivo ?? false) && <LimiteDoPrograma programa={programa} />}
        </ChaveComExplicacao>
      </Stack>
      <SituacaoDoPrograma programa={programa} />
    </QuadroCashback>
  )
}

/** Programa inativo trava as duas opções, então o aviso não depende delas. Sem saber como ele está, pede para conferir. */
function SituacaoDoPrograma({ programa }: { programa: ProgramaCashbackLoja | undefined }): JSX.Element {
  if (programa?.disponivel && !programa.programaAtivo) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        O programa está <strong>inativo</strong> no painel <strong>Sischef Cashback</strong>: enquanto estiver assim, o
        cliente não ganha nem usa cashback pelo cardápio, mesmo com as opções acima ligadas.
      </Alert>
    )
  }

  if (programa?.disponivel) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        O programa está ativo no painel <strong>Sischef Cashback</strong>. O percentual, o limite de uso por pedido e as
        regras ficam lá.
      </Alert>
    )
  }

  // Carregando, ou cashback fora do ar.
  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      As duas opções só funcionam com o programa ativo no painel <strong>Sischef Cashback</strong>: confira lá se ele
      está. O percentual, o limite de uso por pedido e as regras também ficam lá.
    </Alert>
  )
}

/** O limite por pedido é um só, o do programa (spec D6), e vale também no caixa: aqui ele só aparece. */
function LimiteDoPrograma({ programa }: { programa: ProgramaCashbackLoja | undefined }): JSX.Element | null {
  // Carregando, cashback fora do ar ou programa inativo (o aviso do fim da seção diz isso): a linha não aparece.
  if (!programa?.disponivel || !programa.programaAtivo) return null

  const temLimite = programa.percentualMaxUso > 0
  const limite = temLimite ? `até ${formatarPercentual(programa.percentualMaxUso)} do valor dos itens` : 'sem limite'
  return (
    <Explicacao>
      <strong>Limite por pedido:</strong> {limite}. Vale também no caixa (PDV); para mudar, use “Máx. do pedido pago com
      saldo” no painel Sischef Cashback.
    </Explicacao>
  )
}

interface ChaveComExplicacaoProps {
  rotulo: string
  ligado: boolean
  aoMudar: (ligado: boolean) => void
  /** Os textos de apoio, um `Explicacao` por parágrafo. */
  children: ReactNode
}

function ChaveComExplicacao({ rotulo, ligado, aoMudar, children }: ChaveComExplicacaoProps): JSX.Element {
  return (
    <FormControlLabel
      control={<Switch checked={ligado} onChange={(_evento, novoValor) => aoMudar(novoValor)} />}
      label={
        <Box sx={{ ml: 1.5 }}>
          <Typography>{rotulo}</Typography>
          {children}
        </Box>
      }
      sx={{ alignItems: 'flex-start', m: 0 }}
    />
  )
}

function Explicacao({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {children}
    </Typography>
  )
}

interface QuadroCashbackProps {
  descricao: string
  children: ReactNode
}

function QuadroCashback({ descricao, children }: QuadroCashbackProps): JSX.Element {
  return (
    <Secao icone={<RedeemIcon />} titulo="Cashback" descricao={descricao}>
      {children}
    </Secao>
  )
}
